import type { HTMLAttributes, ReactNode } from 'react';
import { cn } from '@/lib/cn';

interface PanelProps extends Omit<HTMLAttributes<HTMLElement>, 'title'> {
  title?: ReactNode;
  actions?: ReactNode;
  padded?: boolean;
  className?: string;
  children: ReactNode;
}

/**
 * The one bordered surface. Never nest a Panel inside a Panel — structure
 * interior content with Eyebrow, Divider and spacing instead.
 */
export function Panel({ title, actions, padded = true, className, children, ...rest }: PanelProps) {
  return (
    <section {...rest} className={cn('corner-brand border border-zinc-800 bg-zinc-900', className)}>
      {title || actions ? (
        <header className="flex h-10 items-center gap-2 border-b border-zinc-800 px-4">
          {title ? (
            <h2 className="truncate text-sm font-semibold text-zinc-50">
              {title}
            </h2>
          ) : null}
          {actions ? <div className="ml-auto flex shrink-0 items-center gap-1.5">{actions}</div> : null}
        </header>
      ) : null}
      <div className={cn('min-w-0', padded && 'p-4')}>{children}</div>
    </section>
  );
}

export function Divider({ label, className }: { label?: string; className?: string }) {
  if (!label) {
    return <hr className={cn('border-zinc-800', className)} />;
  }
  return (
    <div role="separator" className={cn('flex items-center gap-3', className)}>
      <span className="h-px flex-1 bg-zinc-800" aria-hidden />
      <span className="text-xs font-medium text-zinc-400">
        {label}
      </span>
      <span className="h-px flex-1 bg-zinc-800" aria-hidden />
    </div>
  );
}
